import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'sg.superAdminDebugOverlay.enabled';
const ENABLED_AT_STORAGE_KEY = 'sg.superAdminDebugOverlay.enabledAt';
const POSITION_STORAGE_KEY = 'sg.superAdminDebugOverlay.position';
const listeners = new Set<() => void>();

export interface SuperAdminDebugOverlayPosition {
  x: number;
  y: number;
}

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

export function getSuperAdminDebugOverlayEnabled() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(STORAGE_KEY) === 'true';
}

export function setSuperAdminDebugOverlayEnabled(enabled: boolean) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, String(enabled));
  if (enabled) {
    if (!window.localStorage.getItem(ENABLED_AT_STORAGE_KEY)) {
      window.localStorage.setItem(ENABLED_AT_STORAGE_KEY, new Date().toISOString());
    }
  } else {
    window.localStorage.removeItem(ENABLED_AT_STORAGE_KEY);
  }
  notifyListeners();
}

export function getSuperAdminDebugOverlayEnabledAt() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(ENABLED_AT_STORAGE_KEY);
}

export function getSuperAdminDebugOverlayPosition(): SuperAdminDebugOverlayPosition | null {
  const raw = getSuperAdminDebugOverlayPositionValue();
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<SuperAdminDebugOverlayPosition>;
    if (typeof parsed.x !== 'number' || typeof parsed.y !== 'number') {
      return null;
    }

    return { x: parsed.x, y: parsed.y };
  } catch {
    return null;
  }
}

export function getSuperAdminDebugOverlayPositionValue() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(POSITION_STORAGE_KEY);
}

export function setSuperAdminDebugOverlayPosition(position: SuperAdminDebugOverlayPosition) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
  notifyListeners();
}

export function subscribeToSuperAdminDebugOverlayPreference(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function useSuperAdminDebugOverlayPreference() {
  return useSyncExternalStore(
    subscribeToSuperAdminDebugOverlayPreference,
    getSuperAdminDebugOverlayEnabled,
    () => false,
  );
}

export function useSuperAdminDebugOverlayPositionPreference() {
  return useSyncExternalStore(
    subscribeToSuperAdminDebugOverlayPreference,
    getSuperAdminDebugOverlayPositionValue,
    () => null,
  );
}

export function useSuperAdminDebugOverlayEnabledAtPreference() {
  return useSyncExternalStore(
    subscribeToSuperAdminDebugOverlayPreference,
    getSuperAdminDebugOverlayEnabledAt,
    () => null,
  );
}
